import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { useSignal } from "@preact/signals";
import asArray from "../../lib/as-array";
import { getChartData, isDataRequest } from "../../lib/chart";
import useDeepMemo from "../../lib/hooks/use-deep-memo";
import { useInterval } from "../../lib/hooks/use-interval";
import QueuedMessageError from "../../lib/errors/queued-message-error";
/**
 * Waits for data to become available before rendering a component. Optionally
 * renders a loading component until the data resolves.
 */
export default function ComponentWithData(props) {
    const isPromise = props.data instanceof Promise;
    const isCallback = typeof props.data === "function";
    const isStaticData = !isPromise && !isCallback && !isDataRequest(props.data);
    const staticData = props.data;
    const data = useSignal(isStaticData ? staticData : null);
    const isLoading = useSignal(!isStaticData);
    const error = useSignal(undefined);
    const requestStatus = useSignal(undefined);
    // We change which memo hook we used based on what kind of data has been provided.
    // When a DataRequest object is provided, we need to use the deep compare hook to accurately
    // determine when the request has changed. When a Promise has been provided, we can't
    // inspect the Promise object to determine whether it's different to the previous one. So we
    // just use the regular memo hook which will always trigger the hook.
    const memoHook = props.data instanceof Promise ? useMemo : useDeepMemo;
    const fetchData = (dataRequestOrPromise) => {
        if (dataRequestOrPromise instanceof Promise) {
            return dataRequestOrPromise;
        }
        const signal = (status) => {
            requestStatus.value = status;
        };
        if (typeof dataRequestOrPromise === "function") {
            const unwrapped = dataRequestOrPromise();
            if (unwrapped instanceof Promise) {
                return unwrapped;
            }
            return getChartData(unwrapped, signal);
        }
        return getChartData(dataRequestOrPromise, signal);
    };
    memoHook(() => {
        if (!isStaticData) {
            // When the data definition changes, reset the state...
            error.value = undefined;
            isLoading.value = true;
            data.value = null;
            // ... and update the state when the data resolves
            fetchData(props.data)
                .then((response) => {
                data.value = response;
                if (props.onData) {
                    props.onData(response);
                }
                isLoading.value = false;
            })
                .catch((caughtError) => {
                if (caughtError.name !== "AbortError") {
                    console.error("[ComponentWithData] Data fetch error", caughtError);
                    isLoading.value = false;
                    error.value = caughtError;
                }
            });
        }
    }, [props.data]);
    // The dataRefreshInterval controls how often data is periodically refreshed. This can be
    // specified as a number of milliseconds, or undefined/null to disable periodic refresh.
    // It can also be specified as a function that takes the existing data before returning
    // a value.
    const dataRefreshInterval = props.dataRefreshInterval;
    const refreshInterval = typeof dataRefreshInterval === "function"
        ? dataRefreshInterval(data.value)
        : dataRefreshInterval;
    useInterval(() => {
        if (!isStaticData) {
            fetchData(props.data).then((response) => {
                data.value = response;
                if (props.onData) {
                    props.onData(response);
                }
            });
        }
    }, refreshInterval);
    if (isLoading.value) {
        if (typeof props.loading === "function") {
            return props.loading(requestStatus.value);
        }
        return props.loading || null;
    }
    if (error.value) {
        if (typeof props.error === "function") {
            return props.error(error.value);
        }
        return typeof props.error !== "undefined" ? props.error : _jsx(DefaultErrorComponent, { error: error.value });
    }
    if (data.value === null || asArray(data.value).length === 0) {
        return typeof props.noData !== "undefined" ? (props.noData) : (_jsx("div", { className: "no-data-message qa-component-no-data", children: _jsx("div", { children: "No data to display. Try changing the filters." }) }));
    }
    try {
        return _jsx(_Fragment, { children: props.component(data.value) });
    }
    catch (renderError) {
        console.error("[ComponentWithData] Render error", renderError);
        error.value = renderError;
        return null;
    }
}
function DefaultErrorComponent({ error }) {
    if (error instanceof QueuedMessageError) {
        return (_jsxs("div", { className: "qa-component-error", children: ["There was an error while fetching the data for this component. When contacting support, please reference message ID ", _jsx("b", { children: error.queueMessage.queueMessageId.substring(0, 16) })] }));
    }
    return _jsx("div", { className: "qa-component-error", children: "This component failed to load." });
}
